import type { ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';

import { RuleType } from '@/types/api';
import { useHelpStore } from '@/stores/help';

import { Tooltip, TooltipContent, TooltipTrigger } from '../tooltip';

/**
 * Props for the HelpTooltip component.
 */
export interface HelpTooltipProps {
  content: string;
  disabled?: boolean;
  showIcon?: boolean;
  children: ReactNode;
  helpTopicId?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * Tooltip component that displays help content with optional link to detailed help topic.
 *
 * @param { HelpTooltipProps } props The component props.
 * @returns { JSX.Element } The rendered tooltip.
 */
export const HelpTooltip = ({
  content,
  children,
  helpTopicId,
  side = 'top',
  disabled = false,
  showIcon = false,
}: HelpTooltipProps) => {
  const { open, selectTopic } = useHelpStore();

  const handleLearnMore = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (helpTopicId) {
      selectTopic(helpTopicId);
      open();
    }
  };

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help">
          {children}
          {showIcon && <HelpCircle className="size-3.5 text-muted-foreground" aria-hidden="true" />}
        </span>
      </TooltipTrigger>
      <TooltipContent side={side} className="max-w-xs">
        <p>{content}</p>
        {helpTopicId && (
          <button
            type="button"
            onClick={handleLearnMore}
            className="mt-1 text-xs text-primary underline hover:text-primary/80"
          >
            Learn more
          </button>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Props for the RuleTypeTooltip component.
 */
export interface RuleTypeTooltipProps {
  ruleType: string;
  children: ReactNode;
}

const RULE_DESCRIPTIONS: Record<
  string,
  {
    short: string;
    topicId?: string;
  }
> = {
  [RuleType.ONE_TO_MANY]: {
    topicId: 'rule-split',
    short: 'Split one field into multiple',
  },
  [RuleType.MANY_TO_ONE]: {
    topicId: 'rule-combine',
    short: 'Merge multiple fields into one',
  },
  [RuleType.ENRICHMENT]: {
    topicId: 'rule-enrichment',
    short: 'Look up values in a static map',
  },
  [RuleType.SYNTHETIC]: {
    topicId: 'rule-synthetic',
    short: 'Generate values (UUID, timestamp)',
  },
  [RuleType.METADATA]: {
    topicId: 'rule-metadata',
    short: 'Extract CDC metadata (LSN, operation)',
  },
  [RuleType.NORMALIZATION]: {
    topicId: 'rule-normalization',
    short: 'Map values using a lookup table',
  },
  [RuleType.CONDITIONAL]: {
    topicId: 'rule-conditional',
    short: 'Logic-based if/else transformations',
  },
  [RuleType.ONE_TO_ONE]: {
    topicId: 'rule-one-to-one',
    short: 'Copy source field to target (same or different name)',
  },
};

/**
 * Tooltip component that displays help for a specific transformation rule type.
 *
 * @param { RuleTypeTooltipProps } props The component props.
 * @returns { JSX.Element } The rendered tooltip.
 */
export const RuleTypeTooltip = ({ ruleType, children }: RuleTypeTooltipProps) => {
  const ruleInfo = RULE_DESCRIPTIONS[ruleType];

  if (!ruleInfo) {
    return <>{children}</>;
  }

  return (
    <HelpTooltip content={ruleInfo.short} helpTopicId={ruleInfo.topicId}>
      {children}
    </HelpTooltip>
  );
};
